<template>
    <v-card class="ma-3 pa-5">
        <div class="csvContainer">
            <v-btn color="primary" small @click="downloadStationMessagesCSV">
                Download CSV
            </v-btn>
        </div>
        <v-data-table
            :expanded.sync="expanded"
            :headers="headers"
            :item-key="tData.id"
            :items="tData"
            :items-per-page="20"
            :loading="loading"
            :options.sync="options"
            :single-expand="singleExpand"
            :page="tableOptions && tableOptions.page ? tableOptions.page : 1"
            :server-items-length="count"
            :footer-props="footerProps"
        >
            <template v-slot:item.locale.et="{ item }">
                <v-textarea
                    v-model="item.locale.et"
                    auto-grow
                    class="ma-0 pa-0"
                    flat
                    hide-details
                    row-height="15"
                    rows="1"
                    solo
                ></v-textarea>
            </template>
            <template v-slot:item.locale.en="{ item }">
                <v-textarea
                    v-model="item.locale.en"
                    auto-grow
                    class="ma-0 pa-0"
                    flat
                    hide-details
                    row-height="15"
                    rows="1"
                    solo
                ></v-textarea>
            </template>
            <template v-slot:item.locale.fi="{ item }">
                <v-textarea
                    v-model="item.locale.fi"
                    auto-grow
                    class="ma-0 pa-0"
                    flat
                    hide-details
                    row-height="15"
                    rows="1"
                    solo
                ></v-textarea>
            </template>
            <template v-slot:item.locale.lt="{ item }">
                <v-textarea
                    v-model="item.locale.lt"
                    auto-grow
                    class="ma-0 pa-0"
                    flat
                    hide-details
                    row-height="15"
                    rows="1"
                    solo
                ></v-textarea>
            </template>
            <template v-slot:item.probes="{ item }">
                <v-select
                    :items="allProbes"
                    item-text="name"
                    item-value="probeId"
                    v-model="item.probes"
                    label="Probes"
                    hint="Pick stations"
                    multiple
                    return-object
                >
                    <template v-slot:selection="{ item: sItem, index }">
                        <span v-if="index === 0">{{ sItem.name || '' }} </span>
                        <span v-if="index === 1" class="grey--text text-caption ml-1"
                            >(+ {{ item.probes.length - 1 }} others)
                        </span>
                    </template>
                    <template v-slot:prepend-item>
                        <v-list-item ripple @mousedown.prevent @click="handleSelectAllClick(item)">
                            <v-list-item-action>
                                <v-icon color="indigo darken-4">
                                    {{ toggleAllIcon(item) }}
                                </v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                                <v-list-item-title>
                                    Select All
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                    </template>
                </v-select>
            </template>
            <template v-slot:item.actions="{ item }">
                <v-icon
                    v-if="
                        user.role === 'super_admin' ||
                            user.user_permissions.station_messages.includes(PermissionsConstants.EDIT)
                    "
                    class="mr-2"
                    @click="editItem(item)"
                >
                    mdi-content-save-outline
                </v-icon>
                <v-icon
                    v-if="
                        user.role === 'super_admin' ||
                            user.user_permissions.station_messages.includes(PermissionsConstants.DELETE)
                    "
                    class="mr-2"
                    @click="deleteItem(item)"
                >
                    mdi-trash-can-outline
                </v-icon>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { PermissionsConstants } from '../../permissions'
import StationMessageService from '@/service/StationMessageService'

export default {
    name: 'StationMessageTable',
    data() {
        return {
            tableDataSize: 0,
            expanded: [],
            singleExpand: false,
            tData: [],
            options: this.tableOptions,
            footerProps: { 'items-per-page-options': [20, 50, 100, 500] },
            headers: [
                { text: 'Text ET', value: 'locale.et', sortable: false, align: 'start', width: '15%' },
                { text: 'Text EN', value: 'locale.en', sortable: false, align: 'start', width: '15%' },
                { text: 'Text FI', value: 'locale.fi', sortable: false, align: 'start', width: '15%' },
                { text: 'Text LT', value: 'locale.lt', sortable: false, align: 'start', width: '15%' },
                { text: 'Probes', value: 'probes', sortable: false, align: 'start', width: '20%' },
                { text: 'Message type', value: 'message_type', sortable: false, align: 'start', width: '10%' },
                { text: '', value: 'actions', sortable: false, width: '10%' },
            ],
        }
    },
    watch: {
        tableData: function() {
            this.tData = this.tableData
        },
    },
    async mounted() {
        await this.getAllStationMessages()
    },
    methods: {
        ...mapActions('stationMessage', ['getAllStationMessages', 'updateStationMessage', 'deleteStationMessage']),
        async editItem(item) {
            await this.updateStationMessage(item)
        },
        async deleteItem(item) {
            if (confirm('Are you sure?')) {
                await this.deleteStationMessage(item.station_message_id)
                await this.getAllStationMessages()
            }
        },
        handleSelectAllClick(item) {
            if (item.probes.length === this.allProbes.length) {
                item.probes = []
            } else {
                item.probes = this.allProbes
            }
        },
        toggleAllIcon(item) {
            if (item.probes.length === this.allProbes.length) return 'mdi-close-box'
            if (item.probes.length > 0) return 'mdi-minus-box'
            return 'mdi-checkbox-blank-outline'
        },
        async downloadStationMessagesCSV() {
            const resp = await StationMessageService.downloadStationMessagesCSV()
            const url = window.URL.createObjectURL(new Blob([resp.data]))

            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'station_messages_table_data.csv')
            document.body.appendChild(link)
            link.click()
        },
    },
    computed: {
        PermissionsConstants() {
            return PermissionsConstants
        },
        ...mapState('stationMessage', ['tableData', 'loading', 'tableOptions', 'count', 'allProbes']),
        ...mapState('auth', ['user']),
    },
}
</script>

<style scoped>
.csvContainer {
    display: flex;
    align-items: center;
    justify-content: end;
    padding: 15px 0;
}

.simple-table {
    padding: 0 !important;
}

.locale {
    padding-left: 1.7rem !important;
    width: 170px !important;
}
</style>
